<template>
  <div>

    <div v-if="!show404 && !show403" class="sx-page">
      <!-- Hero, menu and tab bar of the system page (US 9370) -->
      <SystemHero v-if="system.id"
          :system="system"
          :summary="summary"
          @edit="openEdit"
          @get-updated="startProject"
          @move-links="openWhereUsed('move')"
          @duplicate="openDuplicate"
          @retire="openWhereUsed('retire')"
          @reactivate="openWhereUsed('reactivate')"
          @delete="showConfirmDeleteModal">
        <template #task>
          <span v-for="task in myTasks" :key="task.id" class="sx-hero__task">
            <UpdateSingleTaskModal :task="task" :editIcon="true" @reloadContent="loadContent"></UpdateSingleTaskModal>
          </span>
        </template>
      </SystemHero>
      <!-- The hero's outline while the system loads, so the tab bar doesn't move down afterwards -->
      <div v-else class="sx-hero is-loading" aria-hidden="true" qid="system-hero-loading">
        <span class="sx-hero__logo"></span>
        <div class="sx-hero__main">
          <span class="sx-skel is-short"></span>
          <span class="sx-skel is-title"></span>
          <span class="sx-skel is-long"></span>
          <span class="sx-skel is-tags"></span>
        </div>
        <div class="sx-hero__people">
          <span class="sx-skel is-short"></span>
          <span class="sx-skel is-avatar"></span>
          <span class="sx-skel is-short"></span>
        </div>
        <div class="sx-hero__done">
          <span class="sx-skel is-ring"></span>
          <span class="sx-skel is-short"></span>
        </div>
        <div class="sx-hero__act">
          <span class="sx-skel is-button"></span>
          <span class="sx-skel is-button"></span>
        </div>
      </div>

      <!-- Our own tab bar: Bootstrap tabs lose their order when tabs of modules appear after the page loads -->
      <nav class="sx-tabs" role="tablist" qid="system-tabs">
        <button v-for="item in tabItems"
                :key="item.key"
                type="button"
                role="tab"
                class="sx-tabs__tab"
                :class="{ on: tab === item.key }"
                :aria-selected="tab === item.key ? 'true' : 'false'"
                :qid="'system-tab-' + item.key"
                @click="handleUpdateTab(item.key)">
          <font-awesome-icon :icon="item.icon"/>{{ item.label }}<span v-if="item.count" class="sx-tabs__n">{{ item.count }}</span>
        </button>
      </nav>

      <div class="sx-tabs__content">
        <!-- Overview: where it runs, data, security and where it's used (US 9370). It stays loaded, as the hero uses its summary. -->
        <div v-show="tab === 'basic-info'" role="tabpanel">
          <SystemOverviewTab v-if="system.id"
              ref="overview"
              :system="system"
              @updated="updateSystem"
              @summary="summary = $event"
              @open-tab="handleUpdateTab"
              @link-contract="openLinkContract"
              @get-updated="startProject"></SystemOverviewTab>
        </div>

        <!-- Where used: every link, moving them to another system, retiring (US 9370) -->
        <div v-show="tab === 'where-used'" role="tabpanel">
          <SystemWhereUsedTab v-if="showWhereUsedTab && system.id"
              ref="whereUsed"
              :key="system.updated_at"
              :system="system"
              :start-with="whereUsedAction"
              @started="whereUsedAction = ''"
              @updated="updateSystem"></SystemWhereUsedTab>
        </div>

        <div v-if="getClient.gdprModule && showDataTab" v-show="tab === 'data'" role="tabpanel">
          <SystemDataFlowTab :system="system" @updated="updateSystem"></SystemDataFlowTab>
        </div>

        <div v-show="tab === 'controls'" role="tabpanel">
          <SystemSecurityTab v-if="showControlsTab && system.id" :system="system" @updated="updateSystem" @open-tab="handleUpdateTab"></SystemSecurityTab>
        </div>

        <div v-if="getClient.riskModule && showRiskTab" v-show="tab === 'risk-management'" role="tabpanel">
          <AssetRiskTab asset="systems" catalogue-type="system" :object="system"
                :can-create="isGranted([permissions.SYSTEM_RISK_SCENARIO_CREATE, permissions.SYSTEM_RISK_SCENARIO_CREATE_OWN], system) && (!getClient.parent || system.partition)"
                :can-edit="isGranted([permissions.SYSTEM_RISK_SCENARIO_EDIT, permissions.SYSTEM_RISK_SCENARIO_EDIT_OWN], system) && (!getClient.parent || system.partition)"
                :can-delete="isGranted([permissions.SYSTEM_RISK_SCENARIO_DELETE, permissions.SYSTEM_RISK_SCENARIO_DELETE_OWN], system) && (!getClient.parent || system.partition)"
                @updated="updateSystem"></AssetRiskTab>
        </div>

        <div v-if="getClient.assessmentModule && showAssessmentsTab" v-show="tab === 'assessments'" role="tabpanel">
          <SystemAssessmentsTab :system="system" @updated="updateSystem"></SystemAssessmentsTab>
        </div>

        <div v-if="showHistoryTab" v-show="tab === 'history'" role="tabpanel">
          <SystemHistory :object="system"></SystemHistory>
        </div>
      </div>

      <SystemEditPanel v-if="system.id" ref="editPanel" :system="system" @saved="afterEdit" @logo="system = Object.assign({}, system, { logo: $event })"></SystemEditPanel>
      <SystemLinkContractPanel v-if="system.id && getClient.contractBasicModule"
          ref="linkPanel"
          :system="system"
          :linked="(summary && summary.contracts) || []"
          @saved="updateSystem"></SystemLinkContractPanel>
      <SystemDeleteModal v-if="system.id"
          ref="deleteModal"
          :system="system"
          :summary="summary"
          @retire="openWhereUsed('retire')"
          @move-links="openWhereUsed('move')"
          @deleted="goToList"></SystemDeleteModal>

      <!-- Duplicate: only the name is asked, the rest is copied -->
      <b-modal ref="duplicate" hide-footer :title="$t('system_hero.duplicate.title')" @shown="$refs.duplicateName.focus()">
        <p class="text-muted">{{ $t('system_hero.duplicate.text') }}</p>
        <b-form @submit.prevent="duplicate">
          <label for="sx-duplicate-name">{{ $t('system_hero.duplicate.name') }}</label>
          <b-form-input id="sx-duplicate-name" ref="duplicateName" v-model="duplicateName" :maxLength="255" required qid="system-duplicate-name"></b-form-input>
          <div class="text-right mt-3">
            <b-button variant="secondary" @click="$refs.duplicate.hide()">{{ $t('systems.create.cancel') }}</b-button>
            <b-button type="submit" variant="success" class="ml-2" :disabled="duplicating || !duplicateName.trim()" qid="system-duplicate-submit">
              <font-awesome-icon :icon="duplicating ? 'spinner' : 'copy'" :class="{ 'fa-spin': duplicating }"/> {{ $t('system_hero.duplicate.submit') }}
            </b-button>
          </div>
        </b-form>
      </b-modal>


      <span class="pm-hidden-trigger">
        <CreateProjectModal v-if="getClient.projectModule"
            ref="createProject"
            type="update"
            :types="['update', 'validation']"
            scope="systems"></CreateProjectModal>
      </span>
    </div>


    <div v-if="show404">
      <page404></page404>
    </div>

    <div v-if="show403">
      <page403></page403>
    </div>

  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import SystemHero from '@/views/Systems/page/SystemHero.vue';
import SystemEditPanel from '@/views/Systems/page/SystemEditPanel.vue';
import SystemOverviewTab from '@/views/Systems/overview/SystemOverviewTab.vue';
import SystemWhereUsedTab from '@/views/Systems/overview/SystemWhereUsedTab.vue';
import CreateProjectModal from '@/views/Projects/CreateProjectModal.vue';
import SystemLinkContractPanel from '@/views/Systems/page/SystemLinkContractPanel.vue';
import SystemDeleteModal from '@/views/Systems/page/SystemDeleteModal.vue';
import SystemDataFlowTab from '@/views/Systems/page/SystemDataFlowTab.vue';
import SystemSecurityTab from '@/views/Systems/page/SystemSecurityTab.vue';
import SystemAssessmentsTab from '@/views/Systems/page/SystemAssessmentsTab.vue';
import SystemHistory from '@/views/Systems/SystemHistory.vue';
import AssetRiskTab from '@/components/risk/AssetRiskTab.vue';
import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';
import page404 from '@/components/404block.vue';
import page403 from '@/components/403block.vue';


export default {
  name: 'SystemView',
  data: function() {
    return {
      system: {},
      // From the Overview tab: completeness, contracts and counts for the hero and the tab bar
      summary: null,
      edit: this.$route.params.edit,
      // The open tab, by its code (the same codes task and guide links use)
      tab: 'basic-info',

      // Tabs
      showControlsTab: false,
      showHistoryTab: false,
      showRiskTab: false,
      showAssessmentsTab: false,
      showDataTab: false,
      showWhereUsedTab: false,
      // A menu action the Where used tab runs once its links are loaded: move, retire or reactivate
      whereUsedAction: '',

      duplicateName: '',
      duplicating: false,
      myTasks: [],
      show404: false,
      show403: false,
    }
  },
  components: {
    SystemHero,
    SystemEditPanel,
    SystemOverviewTab,
    SystemWhereUsedTab,
    CreateProjectModal,
    SystemHistory,
    AssetRiskTab,
    SystemAssessmentsTab,
    SystemLinkContractPanel,
    SystemDeleteModal,
    SystemDataFlowTab,
    SystemSecurityTab,
    UpdateSingleTaskModal,
    page403,
    page404
  },
  computed: {
    getClient() {
      return this.$store.state.client
    },
    getTabIndex() {
      return this.$store.state.tabIndex
    },
    getReloadContent() {
      return this.$store.state.updateContent
    },
    getReloadPage() {
      return this.$store.state.updatePage
    },
    counts() {
      return (this.summary && this.summary.counts) || {};
    },
    // Tab positions, as tabs of modules that are off are left out
    // Tabs of modules that are off are left out
    tabItems() {
      let counts = this.counts;
      let client = this.getClient;
      return [
        { key: 'basic-info', icon: 'th-large', label: this.$t('system_page.tab.overview') },
        { key: 'where-used', icon: 'link', label: this.$t('system_where_used.tab'), count: counts.where_used },
        { key: 'data', icon: 'database', label: this.$t('system_hero.tabs.data'), count: counts.data_categories, module: client.gdprModule },
        { key: 'controls', icon: 'shield-alt', label: this.$t('system_page.security.title'), count: counts.measures },
        { key: 'risk-management', icon: 'exclamation-triangle', label: this.$t('systems.tab.risk_management'), count: counts.risks, module: client.riskModule },
        { key: 'assessments', icon: 'chart-line', label: this.$t('systems.tab.assessments'), module: client.assessmentModule },
        { key: 'history', icon: 'history', label: this.$t('systems.tab.history') }
      ].filter(item => item.module === undefined || item.module);
    }
  },
  watch: {
    // Watch when client changes in store and trigger api reload
    getClient: function (newValue, oldValue) {
      let self = this;
      if (newValue && oldValue && newValue.slug && oldValue.slug && newValue.slug !== oldValue.slug) {
        setTimeout(function(){
          if (self.$route.name == 'systems.view') {
            self.goToList()
          }
        }, 800);
      } else {
        this.loadContent();
      }
    },
    getReloadContent: function () {
      if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
        if (this.$store.state.updatedObject.id == this.system.id && this.$store.state.updatedObject.who_am_i == this.system.who_am_i) {
          this.system = this.$store.state.updatedObject
        }
      }
    },
    getReloadPage: function () {
      if (this.$store.state.updatedObject && this.$store.state.updatedObject.id && this.$store.state.updatedObject.id == this.system.id && this.$store.state.updatedObject.who_am_i == this.system.who_am_i) {
        this.$router.push({ name: 'systems.view', params: { slug: this.$store.state.updatedObject.slug }, query: { task: this.$route.query.task }})
      } else {
        this.$router.push({ name: 'systems.view', params: { slug: this.system.slug }, query: { task: this.$route.query.task }})
      }
    },
    getTabIndex: function (newValue) {
      this.handleUpdateTab(newValue)
    },
    '$route.params.slug': function (value, old) {
      if (value && value !== old) {
        this.loadContent();
      }
    }
  },
  methods: {
    historyTabClicked() {
      this.showHistoryTab = true;
    },
    whereUsedTabClicked() {
      this.showWhereUsedTab = true;
    },
    controlsTabClicked() {
      this.showControlsTab = true;
    },
    basicTabClicked() {
    },
    riskTabClicked() {
      this.showRiskTab = true;
    },
    assessmentsTabClicked() {
      this.showAssessmentsTab = true;
    },
    dataTabClicked() {
      this.showDataTab = true;
    },
    goToList() {
      this.$router.push('/systems')
    },
    startProject() {
      this.$refs.createProject.showCreateProjectModal();
    },
    // Task, guide and section links open a tab by its code
    handleUpdateTab(response) {
      let opens = {
        'basic-info': this.basicTabClicked,
        'where-used': this.whereUsedTabClicked,
        'data': this.dataTabClicked,
        'controls': this.controlsTabClicked,
        'risk-management': this.riskTabClicked,
        'assessments': this.assessmentsTabClicked,
        'history': this.historyTabClicked
      };
      // Contracts are linked in Where it runs on the Overview
      if (response === 'relations-third-parties') {
        response = 'basic-info';
      }
      if (opens[response] && this.tabItems.some(item => item.key === response)) {
        this.tab = response;
        opens[response]();
      }
    },
    openEdit() {
      this.$refs.editPanel.show();
    },
    afterEdit(system) {
      if (system.slug && system.slug !== this.system.slug) {
        this.$router.replace({ name: 'systems.view', params: { slug: system.slug } });
        return;
      }
      this.loadContent();
    },
    // Move links, retire and reactivate are done on the Where used tab, which shows what is linked first
    openWhereUsed(action) {
      let tab = this.$refs.whereUsed;
      this.handleUpdateTab('where-used');
      if (tab && tab.loaded) {
        tab.start(action);
      } else {
        this.whereUsedAction = action;
      }
    },
    openDuplicate() {
      this.duplicateName = this.$t('system_hero.duplicate.default_name', { name: this.system.name });
      this.$refs.duplicate.show();
    },
    duplicate() {
      let self = this;
      this.duplicating = true;
      piincHttp.post('systems/' + this.system.slug + '/duplicate', { client: this.getClient.slug, name: this.duplicateName.trim() }).then(function(response) {
        self.duplicating = false;
        self.$refs.duplicate.hide();
        let skipped = response.data.skipped_links;
        if (skipped) {
          self.$toastr('warning', self.$tc('system_hero.duplicate.skipped', skipped, { count: skipped }));
        } else {
          self.$toastr('success', self.$t('system_hero.duplicate.success'));
        }
        self.$router.push({ name: 'systems.view', params: { slug: response.data.slug } });
      }, function() {
        self.duplicating = false;
      });
    },
    showConfirmDeleteModal() {
      this.$refs.deleteModal.show();
    },
    // Without a link, a new contract is linked; with one, that link is changed
    openLinkContract(link) {
      this.$refs.linkPanel.show(link);
    },
    // Changes on other tabs (measures, links) don't touch the system itself, so the summary is reloaded too
    updateSystem() {
      this.loadContent();
      if (this.$refs.overview) {
        this.$refs.overview.loadSummary();
      }
    },
    loadContent() {
      let self = this;
      piincHttp.get('systems/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug,
        }
      }).then(function(response) {
        self.system = response.data;
        self.loadTasks();
        if (self.edit) {
          self.edit = false;
          self.$nextTick(self.openEdit);
        }
      }, function(error) {
        if (error.status == 404) {
          self.show404 = true
        }
        if (error.status == 403) {
          self.show403 = true
        }
      });
    },
    // Tasks asking this user to update the system's details or description, shown in the hero
    loadTasks() {
      let self = this;
      if (!this.getClient.projectModule) {
        return;
      }
      let requests = ['update_basic_info', 'update_description'].map(code => piincHttp.get('projects/me/tasks', { params:
        {
          per_page: 1,
          status: 'pending',
          model_type: this.system.who_am_i,
          model_id: this.system.id,
          code: code,
          client: this.getClient.slug
        }
      }));
      Promise.all(requests).then(function(responses) {
        self.myTasks = [].concat.apply([], responses.map(response => response.data.items));
      }, function() {});
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('storeTabIndex', '');
    next()
  },
  created () {
    let self = this;
    if (this.getClient && this.getClient.slug) {
      this.loadContent();
    }

    // Validation links open the controls or risk tab and highlight the field
    if (this.$route.params.tabIndex && this.$route.params.validation) {
      setTimeout(function(){
        self.handleUpdateTab(self.$route.params.tabIndex == 2 ? 'risk-management' : 'controls');
      }, 1000);
      setTimeout(function(){
        var element = document.getElementsByClassName(self.$route.params.validation);
        if (element.length) {
          element[0].scrollIntoView(true);
          window.scrollBy(0, -100);
          element[0].classList.add("highlight-validation");
        }
      }, 3000);
    }
  }
}
</script>
